import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Bell, QrCode, MessageSquare, Layout, ChevronRight, Lock, Check, Star, Home, DollarSign, BookOpen, LogIn } from 'lucide-react';
import LoadingScreen from '@/components/LoadingScreen';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FEATURE_ICONS = [Bell, QrCode, MessageSquare, Layout];

export default function Landing() {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    axios.get(`${API}/site-settings`).then(r => {
      setSettings(r.data);
      // Warm the loading-splash cache: store the PATH (not a blob URL).
      try {
        const path = r.data?.loading_logo_path;
        if (path) {
          localStorage.setItem('olafel_loading_logo_path', path);
        } else {
          localStorage.removeItem('olafel_loading_logo_path');
        }
      } catch { /* ignore */ }
    }).catch(() => setSettings({}));
  }, []);

  // Show loader while initial site-settings is fetching to avoid flash of stale logo/images
  if (settings === null) return <LoadingScreen />;

  const s = settings || {};
  const features = [
    { icon: FEATURE_ICONS[0], title: s.feature_1_title || 'Smart Call Button', desc: s.feature_1_desc || 'Customers tap to call for service with customizable reasons.' },
    { icon: FEATURE_ICONS[1], title: s.feature_2_title || 'QR Code per Table', desc: s.feature_2_desc || 'Each table gets a unique QR code linking to your digital menu.' },
    { icon: FEATURE_ICONS[2], title: s.feature_3_title || 'Live Chat', desc: s.feature_3_desc || 'Real-time messaging between host and customers at each table.' },
    { icon: FEATURE_ICONS[3], title: s.feature_4_title || 'Layout Manager', desc: s.feature_4_desc || 'Design your restaurant floor plan and manage table positions.' },
  ];

  const heroTitle = s.hero_title || 'Your Tables, Connected.';
  const heroTitleParts = heroTitle.split(',');
  const siteName = s.site_name || 'CallButton';
  const social = s.social_links || {};
  const packages = s.packages || [];
  const headerLogoSize = s.header_logo_size || 36;
  const footerLogoSize = s.footer_logo_size || 28;

  const scrollToPricing = () => {
    document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-stone-50 pb-24 md:pb-0">
      {/* Nav (top) — full menu on md+, compact on mobile */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-stone-200/60">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-3 md:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            {s.site_logo_path ? <SiteLogo path={s.site_logo_path} size={headerLogoSize} /> : (
              <img src="/olafel_logo.png" alt="Olafel" style={{ width: `${headerLogoSize}px`, height: `${headerLogoSize}px` }} className="object-contain" />
            )}
            <span className="text-lg md:text-xl font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>{siteName}</span>
          </div>
          {/* Desktop / Tablet links — hidden on small screens */}
          <div className="hidden md:flex items-center gap-3">
            <Button data-testid="nav-pricing-btn" variant="ghost" onClick={scrollToPricing} className="text-stone-600 hover:text-stone-900 font-medium">Pricing</Button>
            {s.how_it_works_enabled !== false && (
              <Link to="/how-it-works">
                <Button data-testid="nav-how-btn" variant="ghost" className="text-stone-600 hover:text-stone-900 font-medium">How It Works</Button>
              </Link>
            )}
            <Link to="/login">
              <Button data-testid="nav-login-btn" variant="ghost" className="text-stone-600 hover:text-stone-900 font-medium">Sign In</Button>
            </Link>
            <Link to="/register">
              <Button data-testid="nav-register-btn" className="bg-orange-600 hover:bg-orange-700 text-white font-semibold px-6">Get Started</Button>
            </Link>
          </div>
          {/* Mobile single CTA — keep one-tap path to register */}
          <div className="md:hidden">
            <Link to="/register">
              <Button data-testid="nav-register-mobile-btn" size="sm" className="bg-orange-600 hover:bg-orange-700 text-white font-semibold">Get Started</Button>
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 pt-20 pb-16">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div className="animate-fade-in-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-orange-100 text-orange-700 text-xs font-bold uppercase tracking-wider mb-6">
              {s.trial_days || 7}-Day Free Trial
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight font-bold text-stone-900 leading-tight" style={{ fontFamily: 'Outfit' }}>
              {heroTitleParts[0]}{heroTitleParts.length > 1 ? ',' : ''}<br />
              {heroTitleParts.length > 1 && <span className="text-orange-600">{heroTitleParts.slice(1).join(',')}</span>}
            </h1>
            <p className="text-base sm:text-lg text-stone-500 mt-6 max-w-lg leading-relaxed">
              {s.hero_subtitle || 'Give your customers a seamless way to call for service, browse your menu, and chat with your team — all from a simple QR code scan.'}
            </p>
            <div className="flex items-center gap-4 mt-8">
              <Link to="/register">
                <Button data-testid="hero-get-started-btn" className="bg-orange-600 hover:bg-orange-700 text-white font-semibold px-8 h-12 text-base rounded-xl shadow-lg shadow-orange-600/20">
                  {s.hero_cta_text || 'Start Free Trial'}
                  <ChevronRight size={18} className="ml-1" />
                </Button>
              </Link>
              <Button variant="outline" onClick={scrollToPricing} className="h-12 px-6 text-base rounded-xl border-stone-300 text-stone-700">
                View Pricing
              </Button>
            </div>
          </div>
          <div className="relative animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-stone-900/10">
              {s.hero_image_path ? <HeroImage path={s.hero_image_path} /> : (
                <img src="/olafel_logo.png" alt="Olafel" className="w-full h-[400px] object-contain bg-stone-100 p-12" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-stone-900/40 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <div className="bg-white/90 backdrop-blur-lg rounded-xl p-4 shadow-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-orange-600 flex items-center justify-center animate-pulse-ring"><Bell className="text-white" size={20} /></div>
                    <div><p className="text-sm font-semibold text-stone-900">Table 5 is calling</p><p className="text-xs text-stone-500">Ready to Order</p></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Showcase boxes (just above "Everything your restaurant needs") */}
      {(s.showcase_image_path || s.showcase_media_path) && (
        <section className="max-w-6xl mx-auto px-6 pt-20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {/* Box 1 — 1×1 image only */}
            <div className="relative rounded-2xl overflow-hidden bg-stone-100 border border-stone-200 aspect-square">
              {s.showcase_image_path ? (
                <ShowcaseImage path={s.showcase_image_path} />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-stone-300 text-sm">Image slot</div>
              )}
            </div>
            {/* Box 2 — 2×1 wide, image or video */}
            <div className="relative md:col-span-2 rounded-2xl overflow-hidden bg-stone-100 border border-stone-200 aspect-[2/1]">
              {s.showcase_media_path ? (
                <ShowcaseMedia path={s.showcase_media_path} type={s.showcase_media_type} />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-stone-300 text-sm">Video / image slot</div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Features */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="text-center mb-14">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>Everything your restaurant needs</h2>
          <p className="text-base text-stone-500 mt-3 max-w-lg mx-auto">Streamline service, delight customers, and keep your team in sync.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((f, i) => (
            <div key={i} className="bg-white rounded-xl border border-stone-200 p-6 hover:-translate-y-1 hover:shadow-md transition-all duration-300">
              <div className="w-11 h-11 rounded-lg bg-orange-100 flex items-center justify-center mb-4"><f.icon className="text-orange-600" size={22} /></div>
              <h3 className="text-lg font-semibold text-stone-900 mb-2" style={{ fontFamily: 'Outfit' }}>{f.title}</h3>
              <p className="text-sm text-stone-500 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      {packages.length > 0 && (
        <section id="pricing" className="max-w-6xl mx-auto px-6 py-20">
          <div className="text-center mb-14">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>Simple, Transparent Pricing</h2>
            <p className="text-base text-stone-500 mt-3 max-w-lg mx-auto">Start with a {s.trial_days || 7}-day free trial. No credit card required.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {packages.map((pkg, i) => (
              <Card key={pkg.id || i} data-testid={`pricing-card-${pkg.id || i}`} className={`border-2 transition-all hover:-translate-y-1 hover:shadow-lg ${pkg.popular ? 'border-orange-500 shadow-lg shadow-orange-500/10' : 'border-stone-200'}`}>
                <CardContent className="p-6 flex flex-col h-full">
                  {pkg.popular && (
                    <Badge className="bg-orange-600 text-white text-[10px] self-start mb-3 gap-1"><Star size={10} /> Most Popular</Badge>
                  )}
                  <h3 className="text-lg font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>{pkg.name}</h3>
                  <div className="mt-3 mb-4">
                    <span className="text-3xl font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>${pkg.price}</span>
                    <span className="text-sm text-stone-500 ml-1">{pkg.period}</span>
                  </div>
                  <ul className="space-y-2 flex-1 mb-5">
                    {(pkg.features || []).map((feat, j) => (
                      <li key={j} className="flex items-center gap-2 text-sm text-stone-600">
                        <Check size={14} className="text-green-600 flex-shrink-0" />{feat}
                      </li>
                    ))}
                  </ul>
                  <Link to="/register">
                    <Button className={`w-full ${pkg.popular ? 'bg-orange-600 hover:bg-orange-700 text-white' : 'bg-stone-900 hover:bg-stone-800 text-white'}`}>
                      Start Free Trial
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
          {s.payment_instructions && (
            <div className="mt-10 max-w-2xl mx-auto text-center">
              <Card className="border-stone-200 bg-stone-50">
                <CardContent className="p-6">
                  <h3 className="text-base font-semibold text-stone-800 mb-2" style={{ fontFamily: 'Outfit' }}>Payment Instructions</h3>
                  <p className="text-sm text-stone-600 whitespace-pre-line">{s.payment_instructions}</p>
                  {s.payment_whatsapp && (
                    <a href={s.payment_whatsapp} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 rounded-xl bg-green-600 hover:bg-green-700 text-white text-sm font-semibold transition-colors">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                      Chat on WhatsApp for Payment
                    </a>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </section>
      )}

      {/* Customers wall — 12-col grid, rows grow as more are added */}
      {Array.isArray(s.customers) && s.customers.length > 0 && (
        <section className="py-16 bg-white border-t border-stone-200">
          <div className="max-w-6xl mx-auto px-6">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold text-stone-900" style={{ fontFamily: 'Outfit' }}>Our Customers</h2>
              <p className="text-sm text-stone-500 mt-2">Trusted by restaurants around the world</p>
            </div>
            <div
              className="grid gap-3 justify-center"
              style={{
                gridTemplateColumns: `repeat(12, ${(s.customers_image_size_px || 120)}px)`,
                maxWidth: 'min(100%, calc(12 * (' + (s.customers_image_size_px || 120) + 'px + 0.75rem)))',
                margin: '0 auto',
              }}
            >
              {s.customers.map((c) => (
                <CustomerTile key={c.id || c.image_path} customer={c} size={s.customers_image_size_px || 120} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="border-t border-stone-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                {s.footer_logo_path ? <SiteLogo path={s.footer_logo_path} size={footerLogoSize} /> : s.site_logo_path ? <SiteLogo path={s.site_logo_path} size={footerLogoSize} /> : (
                  <img src="/olafel_logo.png" alt="Olafel" style={{ width: `${footerLogoSize}px`, height: `${footerLogoSize}px` }} className="object-contain" />
                )}
                <span className="text-sm font-semibold text-stone-700" style={{ fontFamily: 'Outfit' }}>{siteName}</span>
              </div>
              {(s.footer_links || []).length > 0 && (
                <div className="flex items-center gap-3 ml-2">
                  {s.footer_links.map((link, i) => (
                    <a key={i} href={link.url} target="_blank" rel="noopener noreferrer" className="text-xs text-stone-400 hover:text-stone-600 transition-colors">{link.label}</a>
                  ))}
                </div>
              )}
            </div>
            {(social.tiktok || social.instagram || social.twitter || social.whatsapp) && (
              <div className="flex items-center gap-3">
                {social.tiktok && <SocialIcon href={social.tiktok} title="TikTok"><svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.98a8.2 8.2 0 004.76 1.52V7.05a4.84 4.84 0 01-1-.36z"/></svg></SocialIcon>}
                {social.instagram && <SocialIcon href={social.instagram} title="Instagram"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg></SocialIcon>}
                {social.twitter && <SocialIcon href={social.twitter} title="Twitter/X"><svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg></SocialIcon>}
                {social.whatsapp && <SocialIcon href={social.whatsapp} title="WhatsApp"><svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg></SocialIcon>}
              </div>
            )}
            <div className="flex items-center gap-4">
              <p className="text-xs text-stone-400">{s.footer_text || 'Restaurant service, simplified.'}</p>
              <Link to="/login" className="text-stone-300 hover:text-stone-500 transition-colors" title="Admin Login"><Lock size={12} /></Link>
            </div>
          </div>
        </div>
      </footer>

      {/* Mobile bottom navigation — visible only on small screens */}
      <nav
        data-testid="public-mobile-bottom-nav"
        className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-lg border-t border-stone-200 safe-bottom"
      >
        <div className="grid grid-cols-4">
          <button
            type="button"
            data-testid="mobile-nav-home"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="flex flex-col items-center justify-center gap-0.5 py-2.5 text-stone-600 active:text-orange-600 active:scale-95 transition-all"
          >
            <Home size={20} />
            <span className="text-[10px] font-medium">Home</span>
          </button>
          <button
            type="button"
            data-testid="mobile-nav-pricing"
            onClick={scrollToPricing}
            className="flex flex-col items-center justify-center gap-0.5 py-2.5 text-stone-600 active:text-orange-600 active:scale-95 transition-all"
          >
            <DollarSign size={20} />
            <span className="text-[10px] font-medium">Pricing</span>
          </button>
          {s.how_it_works_enabled !== false ? (
            <Link
              to="/how-it-works"
              data-testid="mobile-nav-how"
              className="flex flex-col items-center justify-center gap-0.5 py-2.5 text-stone-600 active:text-orange-600 active:scale-95 transition-all"
            >
              <BookOpen size={20} />
              <span className="text-[10px] font-medium">How</span>
            </Link>
          ) : (
            <span className="flex flex-col items-center justify-center gap-0.5 py-2.5 text-stone-300">
              <BookOpen size={20} />
              <span className="text-[10px] font-medium">How</span>
            </span>
          )}
          <Link
            to="/login"
            data-testid="mobile-nav-signin"
            className="flex flex-col items-center justify-center gap-0.5 py-2.5 text-stone-600 active:text-orange-600 active:scale-95 transition-all"
          >
            <LogIn size={20} />
            <span className="text-[10px] font-medium">Sign In</span>
          </Link>
        </div>
      </nav>
    </div>
  );
}

function SocialIcon({ href, title, children }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center transition-colors text-stone-600" title={title}>
      {children}
    </a>
  );
}

function SiteLogo({ path, size = 36 }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!path) return;
    let revoke;
    axios.get(`${API}/files/${path}`, { responseType: 'blob' }).then(r => { const url = URL.createObjectURL(r.data); revoke = url; setSrc(url); }).catch(() => {});
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [path]);
  if (!src) return <img src="/olafel_logo.png" alt="Olafel" style={{ width: `${size}px`, height: `${size}px` }} className="object-contain" />;
  return <img src={src} alt="Site logo" className="rounded-lg object-cover" style={{ width: `${size}px`, height: `${size}px` }} />;
}

function HeroImage({ path }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!path) return;
    let revoke;
    axios.get(`${API}/files/${path}`, { responseType: 'blob' }).then(r => { const url = URL.createObjectURL(r.data); revoke = url; setSrc(url); }).catch(() => {});
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [path]);
  if (!src) return <div className="w-full h-[400px] bg-stone-100 animate-pulse" />;
  return <img src={src} alt="Hero" className="w-full h-[400px] object-cover" />;
}

function CustomerTile({ customer, size }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!customer?.image_path) return;
    let revoke;
    axios.get(`${API}/files/${customer.image_path}`, { responseType: 'blob' })
      .then(r => { const url = URL.createObjectURL(r.data); revoke = url; setSrc(url); })
      .catch(() => {});
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [customer?.image_path]);
  return (
    <div
      className="relative overflow-hidden rounded-lg bg-stone-100 border border-stone-200 group"
      style={{ width: `${size}px`, height: `${size}px` }}
    >
      {src ? (
        <img src={src} alt={customer.country || 'Customer'} className="absolute inset-0 w-full h-full object-cover" />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-stone-300 text-xs">…</div>
      )}
      {customer.country && (
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1.5">
          <p className="text-white text-xs font-semibold text-center truncate" style={{ fontFamily: 'Outfit' }}>{customer.country}</p>
        </div>
      )}
    </div>
  );
}

function ShowcaseImage({ path }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!path) return;
    let revoke;
    axios.get(`${API}/files/${path}`, { responseType: 'blob' })
      .then(r => { const url = URL.createObjectURL(r.data); revoke = url; setSrc(url); })
      .catch(() => {});
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [path]);
  if (!src) return <div className="w-full h-full bg-stone-100" />;
  return <img src={src} alt="Showcase" className="w-full h-full object-cover" />;
}

function ShowcaseMedia({ path, type }) {
  const [src, setSrc] = useState(null);
  const [mime, setMime] = useState(null);
  useEffect(() => {
    if (!path) return;
    let revoke;
    axios.get(`${API}/files/${path}`, { responseType: 'blob' })
      .then(r => {
        setMime(r.data?.type || '');
        const url = URL.createObjectURL(r.data);
        revoke = url;
        setSrc(url);
      })
      .catch(() => {});
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [path]);
  if (!src) return <div className="w-full h-full bg-stone-100" />;
  // Prefer explicit type stored in settings; fall back to detected mime
  const isVideo = type === 'video' || (mime && mime.startsWith('video/'));
  if (isVideo) {
    return (
      <video
        src={src}
        className="w-full h-full object-cover"
        autoPlay
        muted
        loop
        playsInline
        controls={false}
      />
    );
  }
  return <img src={src} alt="Showcase" className="w-full h-full object-cover" />;
}

